// src/components/controlRondas/NFCReader.jsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Smartphone, X, HelpCircle } from 'lucide-react';
import './styles/NFCReader.scss';

const NFCReader = ({ onTagRead, onCancel }) => {
  const [nfcSupported, setNfcSupported] = useState(null);
  const [isReading, setIsReading] = useState(false);
  const [error, setError] = useState(null);
  const [readData, setReadData] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    // Comprobar si el navegador soporta NFC
    if ('NDEFReader' in window) {
      setNfcSupported(true);
    } else {
      setNfcSupported(false);
      setError('Tu dispositivo no soporta la lectura NFC o no está activada en el navegador.');
    }
  }, []);

  const startReading = async () => {
    if (!nfcSupported) return;
    
    setIsReading(true);
    setError(null);
    setReadData(null);
    
    try {
      const ndef = new window.NDEFReader();
      
      // Primero solicitar permiso explícitamente
      try {
        await ndef.scan();
        console.log("Escaneo NFC iniciado correctamente.");
      } catch (error) {
        // Manejar errores de permisos
        if (error.name === "NotAllowedError") {
          throw new Error("Permiso para usar NFC denegado. Por favor, permite el acceso a NFC.");
        } else if (error.name === "NotReadableError") {
          throw new Error("No se puede conectar al adaptador NFC. Asegúrate de que NFC esté activado.");
        } else {
          throw error;
        }
      }
      
      ndef.addEventListener("reading", ({ message, serialNumber }) => {
        console.log("NFC Tag leído:", serialNumber);
        
        // Procesar el contenido del tag
        let tagData = {
          id: serialNumber,
          data: {}
        };
        
        // Intentar leer registros NDEF
        if (message && message.records) {
          message.records.forEach((record, index) => {
            if (record.recordType === "text") {
              const textDecoder = new TextDecoder();
              const text = textDecoder.decode(record.data);
              tagData.data[`record_${index}`] = text;
            } else if (record.recordType === "url") {
              const textDecoder = new TextDecoder();
              const url = textDecoder.decode(record.data);
              tagData.data[`record_${index}`] = url;
            }
          });
        }
        
        setReadData(tagData);
        setIsReading(false);
        
        // Añadir vibración como feedback táctil (útil en dispositivos móviles)
        if (navigator.vibrate) {
          navigator.vibrate(200);
        }
        
        // Enviar los datos al componente padre después de un pequeño retraso
        // para que el usuario pueda ver que se ha leído
        setTimeout(() => {
          onTagRead(tagData);
        }, 1000);
      });
      
      ndef.addEventListener("error", (error) => {
        console.error("Error de lectura NFC:", error);
        setError(`Error al leer la etiqueta NFC: ${error.message}`);
        setIsReading(false);
      });
      
    } catch (error) {
      console.error("Error al iniciar la lectura NFC:", error);
      
      // Mensajes de error más descriptivos
      if (error.name === "NotSupportedError") {
        setError("Tu navegador no soporta Web NFC API o está desactivado.");
      } else if (error.name === "NotAllowedError") {
        setError("Permiso para usar NFC denegado. Por favor, permite el acceso a NFC.");
      } else if (error.name === "NotReadableError") {
        setError("No se puede conectar al adaptador NFC. Asegúrate de que NFC esté activado en tu dispositivo.");
      } else if (error.name === "AbortError") {
        setError("La operación NFC ha sido cancelada.");
      } else {
        setError(`No se pudo iniciar la lectura NFC: ${error.message}`);
      }
      
      setIsReading(false);
    }
  };

  const stopReading = () => {
    setIsReading(false);
    onCancel();
  };

  return (
    <div className="nfc-reader-overlay">
      <div className="nfc-reader">
        <div className="nfc-reader-header">
          <h3 className="title">Lector NFC</h3>
          <div className="header-actions">
            <button 
              onClick={() => setShowHelp(!showHelp)}
              className="btn-help"
              title="Ayuda"
            >
              <HelpCircle size={20} />
            </button>
            <button 
              onClick={stopReading}
              className="btn-close"
              title="Cerrar"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {showHelp && (
          <div className="help-container">
            <h4>Cómo usar el lector NFC</h4>
            <ol>
              <li>Asegúrate de que NFC esté activado en tu dispositivo (Ajustes &gt; Conexiones)</li>
              <li>Pulsa "Iniciar Lectura NFC"</li>
              <li>Acerca la parte posterior de tu teléfono a la etiqueta NFC</li>
              <li>Mantén el teléfono quieto hasta que se confirme la lectura</li>
            </ol>
            <p>Nota: La lectura NFC solo funciona en dispositivos Android con Chrome o Edge.</p>
          </div>
        )}
        
        <div className="nfc-reader-content">
          {nfcSupported === null ? (
            <div className="loading">Comprobando disponibilidad NFC...</div>
          ) : nfcSupported === false ? (
            <div className="error-message">
              <AlertCircle size={32} className="error-icon" />
              <p>{error}</p>
              <p className="note">Nota: La lectura de NFC en navegadores solo funciona en Android con Chrome o Edge. No funciona en iOS o en navegadores de escritorio.</p>
              <button 
                onClick={stopReading}
                className="btn btn-secondary"
              >
                Cerrar
              </button>
            </div>
          ) : isReading ? (
            <div className="scanning">
              <div className="nfc-animation">
                <Smartphone size={48} className="nfc-icon" />
                <div className="nfc-waves"></div>
              </div>
              <p className="scanning-text">Acerca tu dispositivo a la etiqueta NFC</p>
              <div className="tips">
                <p>Mueve lentamente el teléfono cerca de la etiqueta</p>
                <p>La etiqueta normalmente se lee mejor desde la parte superior trasera del móvil</p>
              </div>
              <button 
                onClick={stopReading}
                className="btn btn-cancel"
              >
                Cancelar
              </button>
            </div>
          ) : readData ? (
            <div className="success-message">
              <CheckCircle size={32} className="success-icon" />
              <p>¡Etiqueta NFC leída correctamente!</p>
              <p className="tag-id">ID: {readData.id}</p>
            </div>
          ) : (
            <div className="start-container">
              <Smartphone size={32} className="nfc-start-icon" />
              <p className="start-text">
                Pulsa el botón para comenzar a leer etiquetas NFC
              </p>
              <button 
                onClick={startReading}
                className="btn btn-primary"
              >
                Iniciar Lectura NFC
              </button>
              <button 
                onClick={stopReading}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
            </div>
          )}
          
          {error && !isReading && nfcSupported && (
            <div className="error-message">
              <AlertCircle size={16} className="error-icon-small" />
              <p>{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NFCReader;